import { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import logoJRS from '../assets/logo_jrs.png';
import api, { getSaldosHora, getFuncionariosResumo, getDepartamentos, updateSaldoHora, deleteSaldoHora } from '../services/api';

const decToHHMM = v => {
  if (v == null) return '—';
  const neg = v < 0;
  const abs = Math.abs(v);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `${neg ? '-' : ''}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const isoToBR = iso => { if (!iso || !iso.includes('-')) return iso; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };
const hoje = () => new Date().toISOString().slice(0, 10);
const parseNumeroProposta = obs => { const m = (obs || '').match(/\[PROPOSTA:(\d{6}-\d{4})\]/); return m ? m[1] : null; };
const parseDocPeriodo = obs => { const m = (obs || '').match(/\[DOC:([^\]|]+)\|([^\]]+)\]/); return m ? { inicio: m[1], fim: m[2] } : null; };
const docPeriodoMinutos = (inicio, fim) => {
  if (!inicio || !fim) return null;
  const [dI, hI = '00:00'] = inicio.split('T');
  const [dF, hF = '00:00'] = fim.split('T');
  const diff = Math.round((new Date(`${dF}T${hF}:00`) - new Date(`${dI}T${hI}:00`)) / 60000);
  return diff > 0 ? diff : null;
};

export default function Proposta() {
  const [saldos, setSaldos] = useState([]);
  const [saldosRaw, setSaldosRaw] = useState({});
  const [funcs, setFuncs] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [filtros, setFiltros] = useState({ funcionario: '', departamento: '', situacao: 'pendente' });
  const [loading, setLoading] = useState(false);

  const [modal, setModal] = useState(false);
  const [itemAtivo, setItemAtivo] = useState(null);
  const [form, setForm] = useState({ data: '', minutos: 0 });
  const [horasInput, setHorasInput] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [obsAutorizacao, setObsAutorizacao] = useState('');
  const [motivoAusencia, setMotivoAusencia] = useState('');
  const [preview, setPreview] = useState(null);
  const [docViewer, setDocViewer] = useState(null);

  useEffect(() => { getFuncionariosResumo().then(r => setFuncs(r.data)); }, []);
  useEffect(() => { getDepartamentos({ ativo: true }).then(r => setDepartamentos(r.data.results || r.data)).catch(() => {}); }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const primeira = await getSaldosHora({ tipo: 'COMPENSAR_DEPOIS', page_size: 200 });
      const data0 = primeira.data;
      let registros = data0.results || data0;
      let next = data0.next || null;
      while (next) {
        const nextPath = next.replace(/^https?:\/\/[^/]+/, '').replace('/api', '');
        const resp = await api.get(nextPath);
        registros = registros.concat(resp.data.results || resp.data);
        next = resp.data.next || null;
      }

      const rawMap = {};
      registros.forEach(s => { rawMap[s.id] = s; });
      setSaldosRaw(rawMap);

      // Carrega todas as propostas (pendentes, aprovadas e reprovadas)
      const propostas = registros.filter(s =>
        s.autorizado === false || s.autorizado === true
      );
      propostas.sort((a, b) => (b.registro_data || '').localeCompare(a.registro_data || ''));
      setSaldos(propostas);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const isReprovada = s => (s.observacao || '').includes('[REPROVADA]');

  const itensFiltrados = saldos.filter(s => {
    if (filtros.funcionario && String(s.registro_funcionario_id) !== String(filtros.funcionario)) return false;
    if (filtros.departamento) {
      const func = funcs.find(f => String(f.id) === String(s.registro_funcionario_id));
      if (!func || String(func.departamento) !== String(filtros.departamento)) return false;
    }
    if (filtros.situacao === 'pendente') return s.autorizado === false && !isReprovada(s);
    if (filtros.situacao === 'aprovada') return s.autorizado === true;
    if (filtros.situacao === 'reprovada') return isReprovada(s);
    return true; // 'todas'
  });

  const abrirModal = (saldo) => {
    const mins = saldo.registro_saldo_horas != null
      ? Math.abs(Math.round(saldo.registro_saldo_horas * 60))
      : 0;
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    setItemAtivo(saldo);
    setForm({ data: hoje(), minutos: mins });
    setHorasInput(`${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`);
    setObsAutorizacao('');
    setMotivoAusencia(saldo.motivo_ausencia || '');
    setModal(true);
  };

  const parseHHMM = str => {
    const [h, m] = (str || '').split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  const salvar = async () => {
    if (!form.data) { alert('Informe a data de compensação.'); return; }
    setSalvando(true);
    try {
      const saldo = saldosRaw[itemAtivo.id];
      // Aprova: garante que a compensação proposta pelo funcionário está presente e marca como autorizado
      const comps = saldo.compensacoes?.length ? saldo.compensacoes : [{ data: form.data, minutos: parseHHMM(horasInput) }];
      const obsBase = (saldo.observacao || '').replace('[REPROVADA]', '').trim();
      const novaObs = obsAutorizacao.trim() ? `${obsBase} ${obsAutorizacao.trim()}`.trim() : obsBase;
      await updateSaldoHora(itemAtivo.id, { ...saldo, compensacoes: comps, autorizado: true, observacao: novaObs, motivo_ausencia: motivoAusencia });
      setModal(false);
      carregar();
    } catch (e) {
      alert(JSON.stringify(e.response?.data || 'Erro ao salvar'));
    } finally { setSalvando(false); }
  };

  const cancelarProposta = async (saldo) => {
    if (!window.confirm('Cancelar esta proposta? O funcionário poderá enviar uma nova.')) return;
    try {
      await deleteSaldoHora(saldo.id);
      carregar();
    } catch (e) {
      alert(JSON.stringify(e.response?.data || 'Erro ao cancelar proposta'));
    }
  };

  const cancelarAprovacao = async (saldo) => {
    if (!window.confirm('Cancelar a aprovação desta proposta? Ela voltará para pendente.')) return;
    try {
      const obsAtual = (saldo.observacao || '').replace('[REPROVADA]', '').trim();
      await updateSaldoHora(saldo.id, { ...saldo, autorizado: false, observacao: obsAtual });
      carregar();
    } catch (e) {
      alert(JSON.stringify(e.response?.data || 'Erro ao cancelar aprovação'));
    }
  };

  const reprovar = async (saldo) => {
    if (!window.confirm('Reprovar esta proposta?')) return;
    try {
      const obsAtual = (saldo.observacao || '').replace('[REPROVADA]', '').trim();
      await updateSaldoHora(saldo.id, { ...saldo, autorizado: false, observacao: `${obsAtual} [REPROVADA]`.trim() });
      carregar();
    } catch (e) {
      alert(JSON.stringify(e.response?.data || 'Erro ao reprovar'));
    }
  };

  const gerarPDF = (saldo) => {
    const func = funcs.find(f => String(f.id) === String(saldo.registro_funcionario_id));
    const cpfFunc = func?.cpf ? func.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '—';
    const comps = saldo.compensacoes || [];
    const totalCompMins = comps.reduce((s, c) => s + (parseInt(c.minutos) || 0), 0);
    const debitoHoras = saldo.registro_saldo_horas != null ? decToHHMM(Math.abs(saldo.registro_saldo_horas)) : '—';
    const numeroProposta = parseNumeroProposta(saldo.observacao);
    const agora = new Date();
    const dataHoje = `${String(agora.getDate()).padStart(2, '0')}/${String(agora.getMonth() + 1).padStart(2, '0')}/${agora.getFullYear()}`;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    const ML = 20, MR = 20, CW = pw - ML - MR;
    let y = 15;

    // Logo
    doc.addImage(logoJRS, 'PNG', pw - MR - 33, y, 33, 13);

    // Título
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(15, 23, 42);
    doc.text('ACORDO DE COMPENSAÇÃO', pw / 2, y + 4, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(71, 85, 105);
    if (numeroProposta) doc.text(`Proposta Nº ${numeroProposta}`, pw / 2, y + 10, { align: 'center' });
    doc.text('JRS SERVIÇOS EMPRESARIAIS LTDA · CNPJ nº 24.028.923/0001-10', pw / 2, numeroProposta ? y + 15 : y + 10, { align: 'center' });

    y += 26;
    doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.3); doc.line(ML, y, pw - MR, y); y += 7;

    const justLine = (line, x, lineY, width) => {
      const words = line.trim().split(' ').filter(w => w.length > 0);
      if (words.length <= 1) { doc.text(line, x, lineY); return; }
      const ww = words.map(w => doc.getTextWidth(w));
      const gap = (width - ww.reduce((a, b) => a + b, 0)) / (words.length - 1);
      let cx = x;
      words.forEach((word, i) => { doc.text(word, cx, lineY); cx += ww[i] + gap; });
    };

    const bloco = (labelBold, texto) => {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(15, 23, 42);
      const lw = doc.getTextWidth(labelBold);
      doc.text(labelBold, ML, y);
      doc.setFont('helvetica', 'normal');
      const firstW = CW - lw - 1.5;
      const p1 = doc.splitTextToSize(texto, firstW)[0];
      justLine(p1, ML + lw + 1.5, y, firstW);
      const resto = doc.splitTextToSize(texto.slice(p1.length).trimStart(), CW);
      resto.forEach((line, i) => { y += 5; if (i === resto.length - 1) doc.text(line, ML, y); else justLine(line, ML, y, CW); });
      y += 6;
    };

    const campo = (label, valor) => {
      doc.setFontSize(8.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(71, 85, 105);
      doc.text(label, ML, y);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(15, 23, 42);
      doc.text(' ' + valor, ML + doc.getTextWidth(label) + 2, y); y += 6.5;
    };

    bloco('EMPREGADOR: ', 'JRS SERVIÇOS EMPRESARIAIS LTDA, inscrita no CNPJ 24.028.923/0001-10 e CRC PE-001898/O-1, com sede na Av. Agamenon Magalhães, nº 4318, sala 1308, Derby, Recife-PE, CEP 52010-040, representada pelo sócio-administrador JOSÉ ROBERTO DOS SANTOS JÚNIOR, brasileiro, casado sob o regime de comunhão parcial de bens, nascido em 29/09/1986, contador, CPF/MF nº 055.450.124-43, portador da carteira de identidade nº 6.381.044 SSP/PE, residente e domiciliado na Rua Airton Senna da Silva, nº 100, Casa 19, Chã de Cruz, Paudalho/PE.');
    y += 2;
    bloco('EMPREGADO/ESTAGIÁRIO: ', `${saldo.registro_funcionario_nome}   inscrito(a) sob o CPF/MF nº ${cpfFunc}.`);
    y += 2;
    bloco('DELIBERAÇÃO: ', 'As partes acima acordam que a jornada não trabalhada devido assuntos pessoais, será compensada, de forma integral, sem prejuízo do salário, conforme detalhado na tabela abaixo. Fica ciente o empregado que o labor nos dias indicados não configura horas extras, uma vez que haverá a respectiva compensação correspondente a integralidade do período na forma legal, conforme indicado.');
    y += 4;

    doc.setDrawColor(226, 232, 240); doc.line(ML, y, pw - MR, y); y += 7;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
    doc.text('PERÍODO DA AUSÊNCIA', ML, y); y += 8;
    campo('Motivo Ausência:', saldo.motivo_ausencia === 'JUSTIFICADO' ? 'Justificado' : saldo.motivo_ausencia === 'INJUSTIFICADO' ? 'Não Justificado' : '—');
    campo('Data da Ausência:', isoToBR(saldo.registro_data) || '—');
    campo('Quantidade de horas não trabalhadas:', debitoHoras);

    y += 3; doc.setDrawColor(226, 232, 240); doc.line(ML, y, pw - MR, y); y += 7;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
    doc.text('PROPOSTA DE COMPENSAÇÃO:', ML, y); y += 8;

    // Tabela com todos os dias
    const col1 = 70, col2 = 50, rowH = 7;
    const tableW = col1 + col2;
    // Cabeçalho da tabela
    doc.setFillColor(241, 245, 249);
    doc.rect(ML, y, tableW, rowH, 'F');
    doc.setDrawColor(209, 213, 219); doc.setLineWidth(0.3);
    doc.rect(ML, y, tableW, rowH * (comps.length + 2), 'S');
    doc.line(ML + col1, y, ML + col1, y + rowH * (comps.length + 2));
    doc.line(ML, y + rowH, ML + tableW, y + rowH);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(71, 85, 105);
    doc.text('Data de Compensação', ML + 4, y + 5);
    doc.text('Horas a Compensar', ML + col1 + 4, y + 5);
    y += rowH;
    // Linhas de dados
    comps.forEach((c, i) => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(15, 23, 42);
      doc.text(isoToBR(c.data) || '—', ML + 4, y + 5);
      doc.text(decToHHMM(parseInt(c.minutos) / 60), ML + col1 + 4, y + 5);
      if (i < comps.length - 1) doc.line(ML, y + rowH, ML + tableW, y + rowH);
      y += rowH;
    });
    // Linha de total
    doc.setDrawColor(209, 213, 219); doc.line(ML, y, ML + tableW, y);
    doc.setFillColor(241, 245, 249); doc.rect(ML, y, tableW, rowH, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(15, 23, 42);
    doc.text('TOTAL', ML + 4, y + 5);
    doc.text(decToHHMM(totalCompMins / 60), ML + col1 + 4, y + 5);
    y += rowH + 12;

    doc.setTextColor(71, 85, 105); doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
    doc.text(`Recife - PE, ${dataHoje}`, ML, y); y += 16;

    // Assinatura 1
    doc.setDrawColor(150, 150, 150); doc.setLineWidth(0.3);
    doc.line(ML, y, ML + 72, y); y += 5;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(15, 23, 42);
    doc.text(saldo.registro_funcionario_nome, ML, y); y += 4.5;
    doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105);
    doc.text(`CPF: ${cpfFunc}`, ML, y); y += 4.5;
    doc.text('Colaborador(a)', ML, y); y += 16;

    // Assinatura 2
    doc.line(ML, y, ML + 72, y); y += 5;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(15, 23, 42);
    doc.text('MARCOS AURÉLIO BRITO DOS SANTOS', ML, y); y += 4.5;
    doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105);
    doc.text('CPF: 023.871.644-90', ML, y); y += 4.5;
    doc.text('Diretor Técnico', ML, y);

    doc.save(`acordo_${numeroProposta || saldo.registro_data}_${saldo.registro_funcionario_nome.split(' ')[0]}.pdf`);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16, marginBottom: 20, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 38, height: 38, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1rem', marginRight: 14, flexShrink: 0 }}>
            <i className="bi bi-clipboard2-check-fill" />
          </div>
          <div>
            <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Gestão de Pessoal · Controle de Ponto</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', letterSpacing: '-0.5px', lineHeight: 1.2 }}>Proposta de Compensação</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 12px', fontWeight: 600 }}>
            {itensFiltrados.length} registro(s)
          </span>
        </div>
      </div>

      {/* Filtros */}
      <div className="filter-bar mb-3">
        <div style={{ flex: 2, minWidth: 160 }}>
          <label className="form-label">Funcionário</label>
          <select className="form-select" value={filtros.funcionario} onChange={e => setFiltros(f => ({ ...f, funcionario: e.target.value }))}>
            <option value="">Todos</option>
            {funcs.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 160 }}>
          <label className="form-label">Departamento</label>
          <select className="form-select" value={filtros.departamento} onChange={e => setFiltros(f => ({ ...f, departamento: e.target.value }))}>
            <option value="">Todos</option>
            {departamentos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
          </select>
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button className="btn-secondary-custom" onClick={carregar}>
            <i className="bi bi-arrow-clockwise" />Atualizar
          </button>
        </div>
      </div>

      {/* Tabs de situação */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[
          { key: 'pendente',  label: 'Pendentes',  icon: 'bi-hourglass-split', active: '#fef9c3', border: '#fde68a', text: '#92400e' },
          { key: 'aprovada',  label: 'Aprovadas',  icon: 'bi-check-circle-fill', active: '#dcfce7', border: '#86efac', text: '#15803d' },
          { key: 'reprovada', label: 'Reprovadas', icon: 'bi-x-circle-fill', active: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
          { key: 'todas',     label: 'Todas',      icon: 'bi-list-ul', active: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
        ].map(tab => {
          const ativo = filtros.situacao === tab.key;
          return (
            <button key={tab.key} onClick={() => setFiltros(f => ({ ...f, situacao: tab.key }))}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 8, fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer', border: `2px solid ${ativo ? tab.border : '#e2e8f0'}`, background: ativo ? tab.active : '#f8fafc', color: ativo ? tab.text : '#64748b' }}>
              <i className={`bi ${tab.icon}`} />{tab.label}
            </button>
          );
        })}
      </div>

      {/* Tabela */}
      <div className="card-ponto">
        <div className="table-responsive">
          <table className="table-ponto" style={{ width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 120 }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 90 }} />
              <col />
              <col style={{ width: 210 }} />
              <col style={{ width: 140 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ paddingLeft: 32 }}>Nº Proposta</th>
                <th>Funcionário</th>
                <th style={{ whiteSpace: 'nowrap' }}>Data</th>
                <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Horas</th>
                <th>Datas propostas</th>
                <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Status</th>
                <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-3"><span className="spinner-border spinner-border-sm text-primary" /></td></tr>
              )}
              {!loading && itensFiltrados.map(s => {
                const mins = s.registro_saldo_horas != null ? Math.abs(Math.round(s.registro_saldo_horas * 60)) : 0;
                const comps = s.compensacoes || [];
                const dataBR = isoToBR(s.registro_data) || '—';
                const numProposta = parseNumeroProposta(s.observacao);
                const reprovada = isReprovada(s);
                const aprovada = s.autorizado === true;
                const pendente = s.autorizado === false && !reprovada;
                const nenhumDiaCompensado = aprovada;
                return (
                  <tr key={s.id}>
                    <td style={{ paddingLeft: 32 }}>
                      {numProposta
                        ? <span style={{ fontFamily: 'monospace', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 7px', fontSize: 'var(--fs-sm)', fontWeight: 700, color: '#1e293b' }}>{numProposta}</span>
                        : <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-subtle)' }}>—</span>
                      }
                    </td>
                    <td style={{ fontWeight: 600, fontSize: 'var(--fs-base)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.registro_funcionario_nome}>{s.registro_funcionario_nome}</td>
                    {(() => {
                      if (s.motivo_ausencia === 'JUSTIFICADO') {
                        const doc = parseDocPeriodo(s.observacao);
                        if (doc) {
                          const [dI] = doc.inicio.split('T');
                          const [dF] = doc.fim.split('T');
                          return (
                            <td style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                              {isoToBR(dI)}{dI !== dF ? <> – {isoToBR(dF)}</> : ''}
                            </td>
                          );
                        }
                      }
                      return <td style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{dataBR}</td>;
                    })()}
                    {(() => {
                      if (s.motivo_ausencia === 'JUSTIFICADO') {
                        const doc = parseDocPeriodo(s.observacao);
                        const docMins = doc ? docPeriodoMinutos(doc.inicio, doc.fim) : null;
                        return (
                          <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                            <span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: 3, padding: '2px 10px', fontSize: 'var(--fs-sm)', fontWeight: 700 }}>
                              {docMins != null ? decToHHMM(docMins / 60) : '—'}
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 3, padding: '2px 10px', fontSize: 'var(--fs-sm)', fontWeight: 700 }}>
                            -{decToHHMM(mins / 60)}
                          </span>
                        </td>
                      );
                    })()}
                    <td>
                      {comps.length === 0
                        ? <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-subtle)' }}>—</span>
                        : comps.map((c, i) => (
                          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 8px', fontSize: 'var(--fs-xs)', fontWeight: 600, color: '#1d4ed8', marginRight: 4, marginBottom: 2 }}>
                            <i className="bi bi-calendar2-event" style={{ fontSize: 'var(--fs-2xs)' }} />
                            {isoToBR(c.data)} · {decToHHMM(parseInt(c.minutos) / 60)}
                          </span>
                        ))
                      }
                    </td>
                    <td style={{ textAlign: 'center', overflow: 'visible' }}>
                      {(() => {
                        const cfg = aprovada
                          ? { bg: '#dcfce7', color: '#15803d', border: '#86efac', icon: 'bi-check-circle-fill', label: 'Aprovada' }
                          : reprovada
                          ? { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', icon: 'bi-x-circle-fill', label: 'Reprovada' }
                          : { bg: '#fef9c3', color: '#854d0e', border: '#fde68a', icon: 'bi-hourglass-split', label: 'Pendente' };
                        const isJust = s.motivo_ausencia === 'JUSTIFICADO';
                        const isInjust = s.motivo_ausencia === 'INJUSTIFICADO';
                        return (
                          <div style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'stretch', border: `1.5px solid ${cfg.border}`, borderRadius: 8, overflow: 'hidden' }}>
                            <div style={{ background: cfg.bg, color: cfg.color, padding: '4px 9px', fontSize: 'var(--fs-xs)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                              <i className={`bi ${cfg.icon}`} style={{ fontSize: 'var(--fs-2xs)' }} />{cfg.label}
                            </div>
                            {(isJust || isInjust) && (
                              <div style={{
                                background: isJust ? '#eff6ff' : '#fff1f2',
                                color: isJust ? '#1d4ed8' : '#be123c',
                                padding: '4px 9px', fontSize: 'var(--fs-2xs)', fontWeight: 700,
                                borderLeft: `1.5px solid ${cfg.border}`,
                                display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap',
                              }}>
                                <i className={isJust ? 'bi bi-file-earmark-check' : 'bi bi-file-earmark-x'} style={{ fontSize: 'var(--fs-2xs)' }} />
                                {isJust ? 'Justificado' : 'Não Justificado'}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign: 'center', overflow: 'visible' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
                        <button className="btn-icon" onClick={() => s.motivo_ausencia === 'JUSTIFICADO' && s.documento ? setDocViewer(s) : setPreview(s)}
                          title={s.motivo_ausencia === 'JUSTIFICADO' && s.documento ? 'Ver documento anexado' : 'Pré-visualizar proposta'}
                          style={{ color: '#475569' }}>
                          <i className="bi bi-eye-fill" />
                        </button>
                        {aprovada && comps.length > 0 && (
                          <button className="btn-icon" onClick={() => gerarPDF(s)} title="Gerar PDF" style={{ color: '#15803d' }}>
                            <i className="bi bi-file-earmark-pdf-fill" />
                          </button>
                        )}
                        {aprovada && (
                          <button className="btn-icon" onClick={() => nenhumDiaCompensado && cancelarAprovacao(s)}
                            title={nenhumDiaCompensado ? 'Cancelar aprovação' : 'Não é possível cancelar — já há datas compensadas'}
                            disabled={!nenhumDiaCompensado}
                            style={{ color: nenhumDiaCompensado ? '#c2410c' : '#cbd5e1' }}>
                            <i className="bi bi-arrow-counterclockwise" />
                          </button>
                        )}
                        {pendente && (
                          <button className="btn-icon" onClick={() => abrirModal(s)} title="Autorizar proposta" style={{ color: '#1d4ed8' }}>
                            <i className="bi bi-check2-circle" />
                          </button>
                        )}
                        {pendente && (
                          <button className="btn-icon" onClick={() => reprovar(s)} title="Reprovar proposta" style={{ color: '#dc2626' }}>
                            <i className="bi bi-x-circle" />
                          </button>
                        )}
                        {pendente && (
                          <button className="btn-icon" onClick={() => cancelarProposta(s)} title="Cancelar proposta — o funcionário poderá enviar uma nova" style={{ color: '#c2410c' }}>
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && itensFiltrados.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <i className="bi bi-clipboard2-check" style={{ fontSize: '2.5rem', opacity: 0.15, color: 'var(--primary)' }} />
                      <p style={{ fontWeight: 600, marginTop: 8 }}>Nenhuma proposta pendente</p>
                      <p style={{ fontSize: 'var(--fs-sm)' }}>Todos os débitos já possuem compensação agendada</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal — Pré-visualização do documento */}
      {preview && (() => {
        const s = preview;
        const func = funcs.find(f => String(f.id) === String(s.registro_funcionario_id));
        const cpfFunc = func?.cpf ? func.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '—';
        const comps = s.compensacoes || [];
        const totalCompMins = comps.reduce((acc, c) => acc + (parseInt(c.minutos) || 0), 0);
        const debitoHoras = s.registro_saldo_horas != null ? decToHHMM(Math.abs(s.registro_saldo_horas)) : '—';
        const numProposta = parseNumeroProposta(s.observacao);
        const agora = new Date();
        const dataHoje = `${String(agora.getDate()).padStart(2, '0')}/${String(agora.getMonth() + 1).padStart(2, '0')}/${agora.getFullYear()}`;
        const reprovadaPrev = isReprovada(s);
        const aprovadaPrev = s.autorizado === true;
        const badgeBg = aprovadaPrev ? '#dcfce7' : reprovadaPrev ? '#fee2e2' : '#fef9c3';
        const badgeColor = aprovadaPrev ? '#15803d' : reprovadaPrev ? '#991b1b' : '#92400e';
        const badgeLabel = aprovadaPrev ? 'Aprovada' : reprovadaPrev ? 'Reprovada' : 'Pendente';
        // Dimensões A4 naturais (px) e cálculo de escala
        const A4_W = 595, A4_H = 842;
        const HEADER_H = 52, MODAL_PAD = 20;
        const availH = window.innerHeight - HEADER_H - MODAL_PAD * 2;
        const availW = Math.min(window.innerWidth * 0.96, 900) - MODAL_PAD * 2;
        const scale = Math.min(availH / A4_H, availW / A4_W) * 0.90;
        const dispW = Math.round(A4_W * scale);
        const dispH = Math.round(A4_H * scale);
        const modalW = dispW + MODAL_PAD * 2;
        const modalH = HEADER_H + dispH + MODAL_PAD * 2;

        const pStyle = { fontSize: 11, lineHeight: 1.6, color: '#1e293b', textAlign: 'justify', marginBottom: 9 };
        const labelStyle = { fontWeight: 700, color: '#1e293b' };
        return (
          <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setPreview(null)}>
            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', width: modalW, height: modalH, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* Cabeçalho */}
              <div style={{ background: 'linear-gradient(135deg,#1e3a8a,#2563eb)', padding: '10px 16px', height: HEADER_H, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <i className="bi bi-eye-fill" style={{ color: '#fff', fontSize: '1rem' }} />
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 'var(--fs-base)' }}>Pré-visualização do Documento</div>
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 'var(--fs-xs)', marginTop: 1 }}>
                      {s.registro_funcionario_nome}
                      {numProposta && <span style={{ marginLeft: 8, background: 'rgba(255,255,255,0.2)', borderRadius: 4, padding: '1px 6px', fontFamily: 'monospace', fontWeight: 700 }}>Nº {numProposta}</span>}
                      <span style={{ marginLeft: 6, background: badgeBg, color: badgeColor, borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>{badgeLabel}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {aprovadaPrev && comps.length > 0 && (
                    <button className="btn-secondary-custom" style={{ color: '#15803d', borderColor: '#86efac' }} onClick={() => { setPreview(null); gerarPDF(s); }}>
                      <i className="bi bi-file-earmark-pdf-fill" /> Gerar PDF
                    </button>
                  )}
                  <button onClick={() => setPreview(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, padding: '4px 9px', cursor: 'pointer', color: '#fff', fontSize: '0.95rem' }}>
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
              </div>

              {/* Área do papel */}
              <div style={{ background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: MODAL_PAD, flex: 1, overflow: 'hidden' }}>
                {/* Wrapper com tamanho visual escalado */}
                <div style={{ width: dispW, height: dispH, overflow: 'hidden', position: 'relative', boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }}>
                  {/* Papel A4 em tamanho natural, escalado */}
                  <div style={{ width: A4_W, height: A4_H, transform: `scale(${scale})`, transformOrigin: 'top left', background: '#fff', fontFamily: 'Arial, sans-serif', padding: '36px 42px', boxSizing: 'border-box', position: 'relative' }}>

                    {/* Topo: logo + título */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', letterSpacing: 0.5 }}>ACORDO DE COMPENSAÇÃO</div>
                        <div style={{ fontSize: 9.5, color: '#0f172a', marginTop: 3, fontWeight: 600 }}>JRS SERVIÇOS EMPRESARIAIS LTDA</div>
                        <div style={{ fontSize: 9.5, color: '#64748b', marginTop: 1 }}>CNPJ nº 24.028.923/0001-10</div>
                      </div>
                      <img src={logoJRS} alt="JRS" style={{ height: 36, objectFit: 'contain', marginLeft: 16 }} />
                    </div>

                    <hr style={{ borderColor: '#e2e8f0', borderTop: '1px solid #e2e8f0', marginTop: 10, marginBottom: 12 }} />

                    <p style={pStyle}><span style={labelStyle}>EMPREGADOR: </span>JRS SERVIÇOS EMPRESARIAIS LTDA, inscrita no CNPJ 24.028.923/0001-10 e CRC PE-001898/O-1, com sede na Av. Agamenon Magalhães, nº 4318, sala 1308, Derby, Recife-PE, CEP 52010-040, representada pelo sócio-administrador JOSÉ ROBERTO DOS SANTOS JÚNIOR, brasileiro, casado sob o regime de comunhão parcial de bens, nascido em 29/09/1986, contador, CPF/MF nº 055.450.124-43, portador da carteira de identidade nº 6.381.044 SSP/PE, residente e domiciliado na Rua Airton Senna da Silva, nº 100, Casa 19, Chã de Cruz, Paudalho/PE.</p>
                    <p style={pStyle}><span style={labelStyle}>EMPREGADO/ESTAGIÁRIO: </span>{s.registro_funcionario_nome}, inscrito(a) sob o CPF/MF nº {cpfFunc}.</p>
                    <p style={pStyle}><span style={labelStyle}>DELIBERAÇÃO: </span>As partes acima acordam que a jornada não trabalhada devido assuntos pessoais, será compensada, de forma integral, sem prejuízo do salário, conforme detalhado na tabela abaixo. Fica ciente o empregado que o labor nos dias indicados não configura horas extras, uma vez que haverá a respectiva compensação correspondente a integralidade do período na forma legal, conforme indicado.</p>

                    <hr style={{ borderColor: '#e2e8f0', borderTop: '1px solid #e2e8f0', margin: '10px 0' }} />

                    <div style={{ fontWeight: 700, fontSize: 11, color: '#0f172a', marginBottom: 7 }}>PERÍODO DA AUSÊNCIA</div>
                    <div style={{ fontSize: 11, marginBottom: 5 }}>
                      <span style={labelStyle}>Motivo Ausência:</span>{' '}
                      {s.motivo_ausencia === 'JUSTIFICADO' ? 'Justificado' : s.motivo_ausencia === 'INJUSTIFICADO' ? 'Não Justificado' : '—'}
                    </div>
                    <div style={{ fontSize: 11, marginBottom: 5 }}><span style={labelStyle}>Data da Ausência:</span> {isoToBR(s.registro_data) || '—'}</div>
                    <div style={{ fontSize: 11, marginBottom: 10 }}><span style={labelStyle}>Quantidade de horas não trabalhadas:</span> {debitoHoras}</div>

                    <hr style={{ borderColor: '#e2e8f0', borderTop: '1px solid #e2e8f0', margin: '10px 0' }} />

                    <div style={{ fontWeight: 700, fontSize: 11, color: '#0f172a', marginBottom: 9 }}>PROPOSTA DE COMPENSAÇÃO:</div>

                    {comps.length === 0
                      ? <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>Nenhuma data de compensação informada.</div>
                      : (
                        <table style={{ width: '55%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 14 }}>
                          <thead>
                            <tr style={{ background: '#f1f5f9' }}>
                              <th style={{ border: '1px solid #d1d5db', padding: '5px 10px', fontWeight: 700, color: '#475569', textAlign: 'left' }}>Data de Compensação</th>
                              <th style={{ border: '1px solid #d1d5db', padding: '5px 10px', fontWeight: 700, color: '#475569', textAlign: 'left' }}>Horas a Compensar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {comps.map((c, i) => (
                              <tr key={i}>
                                <td style={{ border: '1px solid #d1d5db', padding: '5px 10px' }}>{isoToBR(c.data) || '—'}</td>
                                <td style={{ border: '1px solid #d1d5db', padding: '5px 10px' }}>{decToHHMM(parseInt(c.minutos) / 60)}</td>
                              </tr>
                            ))}
                            <tr style={{ background: '#f1f5f9' }}>
                              <td style={{ border: '1px solid #d1d5db', padding: '5px 10px', fontWeight: 700 }}>TOTAL</td>
                              <td style={{ border: '1px solid #d1d5db', padding: '5px 10px', fontWeight: 700 }}>{decToHHMM(totalCompMins / 60)}</td>
                            </tr>
                          </tbody>
                        </table>
                      )
                    }

                    <div style={{ fontSize: 10, color: '#475569', marginBottom: 22 }}>Recife - PE, {dataHoje}</div>

                    {/* Assinaturas */}
                    <div style={{ display: 'flex', gap: 60 }}>
                      <div>
                        <div style={{ borderTop: '1px solid #aaa', width: 200, marginBottom: 4 }} />
                        <div style={{ fontWeight: 700, fontSize: 10, color: '#0f172a' }}>{s.registro_funcionario_nome}</div>
                        <div style={{ fontSize: 9.5, color: '#475569' }}>CPF: {cpfFunc}</div>
                        <div style={{ fontSize: 9.5, color: '#475569' }}>Colaborador(a)</div>
                      </div>
                      <div>
                        <div style={{ borderTop: '1px solid #aaa', width: 200, marginBottom: 4 }} />
                        <div style={{ fontWeight: 700, fontSize: 10, color: '#0f172a' }}>MARCOS AURÉLIO BRITO DOS SANTOS</div>
                        <div style={{ fontSize: 9.5, color: '#475569' }}>CPF: 023.871.644-90</div>
                        <div style={{ fontSize: 9.5, color: '#475569' }}>Diretor Técnico</div>
                      </div>
                    </div>

                    {/* Rodapé com número da proposta */}
                    {numProposta && (
                      <div style={{ position: 'absolute', bottom: 28, left: 42, right: 42, borderTop: '1px solid #e2e8f0', paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 8.5, color: '#94a3b8' }}>JRS SERVIÇOS EMPRESARIAIS LTDA · CNPJ nº 24.028.923/0001-10</span>
                        <span style={{ fontSize: 8.5, color: '#64748b', fontFamily: 'monospace', fontWeight: 700 }}>Proposta Nº {numProposta}</span>
                      </div>
                    )}

                    {/* Marca d'água */}
                    {!aprovadaPrev && (
                      <div style={{ position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%,-50%) rotate(-30deg)', fontSize: 54, fontWeight: 900, color: reprovadaPrev ? 'rgba(220,38,38,0.1)' : 'rgba(234,179,8,0.13)', pointerEvents: 'none', whiteSpace: 'nowrap', letterSpacing: 8, userSelect: 'none' }}>
                        {reprovadaPrev ? 'REPROVADA' : 'PENDENTE'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal — Visualização de documento justificativo */}
      {docViewer && (() => {
        const s = docViewer;
        const doc = parseDocPeriodo(s.observacao);
        const docMins = doc ? docPeriodoMinutos(doc.inicio, doc.fim) : null;
        const docUrl = `http://localhost:8000${s.documento}`;
        const ext = (s.documento || '').split('.').pop().toLowerCase();
        const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
        const HEADER_H = 52;
        const modalW = Math.min(window.innerWidth * 0.94, 860);
        const modalH = Math.min(window.innerHeight * 0.92, 720);
        return (
          <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setDocViewer(null)}>
            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', width: modalW, height: modalH, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* Cabeçalho */}
              <div style={{ background: 'linear-gradient(135deg,#1e3a8a,#2563eb)', padding: '10px 16px', height: HEADER_H, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <i className="bi bi-file-earmark-text-fill" style={{ color: '#fff', fontSize: '1.1rem' }} />
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 'var(--fs-base)' }}>Documento Justificativo</div>
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 'var(--fs-xs)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{s.registro_funcionario_nome}</span>
                      {doc && (
                        <span style={{ background: 'rgba(255,255,255,0.18)', borderRadius: 4, padding: '1px 7px', fontWeight: 600 }}>
                          {(() => {
                            const [dI] = doc.inicio.split('T');
                            const [dF] = doc.fim.split('T');
                            return dI === dF ? isoToBR(dI) : `${isoToBR(dI)} – ${isoToBR(dF)}`;
                          })()}
                          {docMins != null && <span style={{ marginLeft: 5 }}>· {decToHHMM(docMins / 60)}</span>}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <a href={docUrl} target="_blank" rel="noopener noreferrer"
                    style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, padding: '4px 11px', cursor: 'pointer', color: '#fff', fontSize: 'var(--fs-xs)', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <i className="bi bi-box-arrow-up-right" /> Abrir
                  </a>
                  <button onClick={() => setDocViewer(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, padding: '4px 9px', cursor: 'pointer', color: '#fff', fontSize: '0.95rem' }}>
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
              </div>

              {/* Área do documento */}
              <div style={{ flex: 1, background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {isImg
                  ? <img src={docUrl} alt="Documento" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }} />
                  : <iframe src={docUrl} title="Documento" style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />
                }
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal — Autorizar proposta */}
      {modal && itemAtivo && (
        <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal-box" style={{ maxWidth: 440 }}>
            <div className="modal-header-custom" style={{ background: 'linear-gradient(135deg,#1e3a8a,#2563eb)' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h5 style={{ color: '#fff', margin: 0 }}>
                    <i className="bi bi-clipboard2-check-fill me-2" />Autorizar Proposta
                  </h5>
                  {parseNumeroProposta(itemAtivo.observacao) && (
                    <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 4, padding: '2px 8px', fontWeight: 700, fontFamily: 'monospace', fontSize: 'var(--fs-sm)', color: '#fff' }}>
                      Nº {parseNumeroProposta(itemAtivo.observacao)}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)', marginTop: 3 }}>
                  {itemAtivo.registro_funcionario_nome}
                </div>
              </div>
              <button className="modal-close" style={{ color: '#fff' }} onClick={() => setModal(false)}>
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="modal-body-custom" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Info débito */}
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Débito</div>
                  <div style={{ fontWeight: 700, color: '#991b1b', fontSize: 'var(--fs-lg)' }}>
                    -{decToHHMM(Math.abs(itemAtivo.registro_saldo_horas))}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Data do débito</div>
                  <div style={{ fontWeight: 600, color: '#7f1d1d', fontSize: '0.9rem' }}>{isoToBR(itemAtivo.registro_data)}</div>
                </div>
              </div>

              {/* Motivo da ausência */}
              <div>
                <label className="form-label" style={{ marginBottom: 8 }}>
                  Motivo da Ausência
                </label>
                <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  {[
                    { v: 'JUSTIFICADO',   label: 'Justificado',     bg: '#fef9c3', color: '#854d0e', icon: 'bi-check-circle-fill' },
                    { v: 'INJUSTIFICADO', label: 'Não Justificado', bg: '#fee2e2', color: '#991b1b', icon: 'bi-x-circle-fill' },
                  ].map(op => (
                    <button key={op.v} type="button"
                      onClick={() => setMotivoAusencia(v => v === op.v ? '' : op.v)}
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        padding: '8px 0', border: 'none', cursor: 'pointer',
                        fontSize: 'var(--fs-sm)', fontWeight: 700,
                        background: motivoAusencia === op.v ? op.bg : '#f8fafc',
                        color:      motivoAusencia === op.v ? op.color : '#94a3b8',
                        borderRight: op.v === 'JUSTIFICADO' ? '1px solid #e2e8f0' : 'none',
                        transition: 'all 0.15s',
                      }}
                    >
                      <i className={`bi ${op.icon}`} />{op.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Datas propostas pelo funcionário */}
              <div>
                <label className="form-label" style={{ marginBottom: 8 }}>
                  Datas propostas pelo funcionário
                </label>
                {(itemAtivo.compensacoes || []).length === 0
                  ? <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-subtle)' }}>Nenhuma data informada pelo funcionário.</div>
                  : (itemAtivo.compensacoes || []).map((c, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', marginBottom: 6 }}>
                      <i className="bi bi-calendar2-event" style={{ color: '#1d4ed8' }} />
                      <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>{isoToBR(c.data)}</span>
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>·</span>
                      <span style={{ fontWeight: 600, color: '#1d4ed8', fontSize: 'var(--fs-sm)' }}>{decToHHMM(parseInt(c.minutos) / 60)}</span>
                    </div>
                  ))
                }
              </div>

              {/* Observação */}
              <div>
                <label className="form-label">
                  Observação
                </label>
                <textarea
                  className="form-control form-control-sm"
                  rows={3}
                  placeholder="Observação opcional para o registro..."
                  value={obsAutorizacao}
                  onChange={e => setObsAutorizacao(e.target.value)}
                  style={{ resize: 'none', fontSize: 'var(--fs-sm)' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
                <button className="btn-secondary-custom" onClick={() => setModal(false)}>
                  <i className="bi bi-x" />Cancelar
                </button>
                <button className="btn-primary-custom" onClick={salvar} disabled={salvando}>
                  {salvando ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check2-circle me-1" />Autorizar</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
